import graphqlRequest from "Services/assignment/assignmentRequest";
import {formatLocale_LowerCaseHyphen} from "Services/locale";
import {addAssignmentForStudent} from "Services/assignment/addAssignment/addAssignment";
import {filterEligibleAssignments, orderAssignmentsByEntityType, orderAssignmentsByAssignmentType} from "Services/assignment/functions";
import GET_ASSIGNMENTS, {CHECK_FOR_SCHOOL_WINDOWS, GET_ASSIGNMENT} from "Services/assignment/getAssignment/getAssignmentGraphql";
import GET_DISTRICT_ASSIGNMENTS from "./getDistrictAssignmentGraphql";
import {ENTITY_TYPE, ASSIGNMENT_STATUS, GRADES} from "Services/assignment/constants";
import { inCurrentScreeningWindow,getScreeningWindowsForSchoolYear } from "../../screeningWindowService";
import { ASSIGNMENT_TYPE } from "../constants";
import { getAssignmentTemplate } from "../getAssignmentTemplate";
import { cleanManifest, transformManifest } from "../manifest";
import {updateBatchAssignments} from "Services/assignment/updateBatchAssignments";
import { logSessionInfo } from "../../logger";
import { skipActivityInManifest } from "../updateManifest";
import { SPANISHCONFIG } from "../../../constants/constants";
import { getUserData } from "../../API";
import { isAssessmentTypeAssignment } from "../../util";
import { FEATURE_FLAGS } from "../../../constants/featureFlags";

/**
 * fetch assignments for an entity
 * @param entityId {string} - entity id (student, classroom, school, or district id) to fetch assignments for
 * @param entityType {string} - entity type associated with the given entity id
 * @param grade {number} - student grade
 * @param school {string} - school id
 * @param district {string} - district id
 * @param isAmiraIsipPilot {boolean} - whether this is an Amira ISIP pilot
 * @param targetAssignmentId {string} - specific assignment id being targeted
 * @returns {Promise<array<assignment>>}
 */
export async function getAssignments(entityId, entityType = ENTITY_TYPE.STUDENT, grade=0, school, district, isAmiraIsipPilot, targetAssignmentId, schoolYear){
  if (entityType === ENTITY_TYPE.STUDENT && !schoolYear) {
    logSessionInfo("school_year_validation_failed", {
      entityId,
      entityType,
      reason: "school_year_missing",
      location: "getAssignments"
    });
    throw new Error('School year is required for student assignment queries but not available.');
  }
  
  const defaultEntities = [
    { entityType: "SCHOOL", entityId: school},
    { entityType: "DISTRICT", entityId: district},
    { entityType: "CLASS", entityId: 1234}  //Placeholder to ensure optimized assignment call
  ];

  const response = await graphqlRequest(GET_ASSIGNMENTS, {
    entityId,
    entityType,
    defaultEntities,
    schoolYears: [schoolYear]
  });
  const {assignment} = response;

  let assignments = assignment;

  // NOTE: For now, we assume no transformation for pilot assessments during testing, but this may be removed for launch
  if (!isAmiraIsipPilot || FEATURE_FLAGS.STUDENT_INBOX) {
    //If there are screening window assignments, grab the current template for school and grade
    //Skip transformation when targetAssignmentId exists to avoid unnecessary getAssignmentTemplate calls
    assignments = await Promise.all(assignment.map(async (a)=>{
      return (entityType === ENTITY_TYPE.STUDENT &&
              a.status === ASSIGNMENT_STATUS.ASSIGNED &&
              (a.entityType === ENTITY_TYPE.DISTRICT || a.entityType === ENTITY_TYPE.SCHOOL) &&
              school &&
              !targetAssignmentId) ? await transformScreeningWindowAssignment(a,grade,school,defaultEntities,a.entityType,schoolYear) : Promise.resolve(a);
    }));
  }

  return assignments;
}

export async function getAssignmentById(assignmentId){
  const response = await graphqlRequest(GET_ASSIGNMENT, {
    assignmentId
  });
  const {assignmentById} = response;

  return assignmentById;
}

export async function getAssignmentByIdForStudent(assignmentId) {
  const assignment = await getAssignmentById(assignmentId);

  return transformAssignment(assignment);
}

export async function getDistrictAssignments(entityId){
  let dateFrom = new Date().toISOString().split('T')[0];
  let inputs = {
    entityId,
    entityType: ENTITY_TYPE.DISTRICT,
    filter: {
      dateFrom,
      status: [ASSIGNMENT_STATUS.ASSIGNED]
    }
  };

  const response = await graphqlRequest(GET_DISTRICT_ASSIGNMENTS, inputs);
  const {assignment} = response;
  return assignment;
}

export async function getSchoolAssignments(entityId){
  let dateFrom = new Date().toISOString().split('T')[0];
  let inputs = {
    entityId,
    entityType: ENTITY_TYPE.SCHOOL,
    filter: {
      dateFrom,
      status: [ASSIGNMENT_STATUS.ASSIGNED]
    }
  };

  const response = await graphqlRequest(GET_DISTRICT_ASSIGNMENTS, inputs);
  const {assignment} = response;
  return assignment;
}

/**
 * fetch assignments for a student entity
 * @param studentId {string}
 * @param isBilingual {boolean} - student's isESL setting
 * @returns {Promise<array<assignment>>}
 */
export async function getAssignmentForStudent(studentId, session, targetAssignmentId, failureReturnUrl, isAmiraIsipPilot) {
  let {isESL,actualGrade,schoolId,districtId,screenerEnabled,assessmentEnabled} = session;

  //Skip everything if all types are disabled
  if(screenerEnabled === false && assessmentEnabled === false) return null;

  const assessmentSchoolYear = getUserData().schoolYear;

  const allAssignments = await getAssignments(studentId, ENTITY_TYPE.STUDENT, actualGrade, schoolId, districtId, isAmiraIsipPilot, targetAssignmentId, assessmentSchoolYear);
  logSessionInfo("all_assignments",{allAssignments});
  let filteredAssignments = allAssignments;
  if (!isAmiraIsipPilot) {
    // Only remove completed district assignments when isAmiraIsipPilot is false
    // For the pilot, we want user to be able to retake the district level assignment as many times as they want
    filteredAssignments = removeCompletedDistrictAssignments(allAssignments,actualGrade,screenerEnabled,assessmentEnabled);
  }
  const pendingAssignments = filterPendingAssignments(filteredAssignments);
  const eligibleAssignments = filterEligibleAssignments(pendingAssignments);

  let nextAssignment = orderAssignmentsByAssignmentType(orderAssignmentsByEntityType(eligibleAssignments))[0];

  if (targetAssignmentId) {
    const targetAssignment = eligibleAssignments.find(assignment => assignment.assignmentId === targetAssignmentId);
    if (targetAssignment) {
      nextAssignment = targetAssignment;
    } else {
      if (failureReturnUrl) {
        window.location.href = failureReturnUrl;
        return;
      }
    }
  }

  if (nextAssignment){
    nextAssignment = await checkEnabledTasks(nextAssignment,session);
    if(nextAssignment.entityType !== ENTITY_TYPE.STUDENT) {
      nextAssignment = await addAssignmentForStudent(nextAssignment, studentId, isESL, assessmentSchoolYear);
    }
    await unassignDuplicateStudentAssignments(nextAssignment, eligibleAssignments);
  }
  logSessionInfo("next_assignment",{nextAssignment});

  if (
    (
      !nextAssignment ||
      !isAssessmentTypeAssignment(nextAssignment.assignmentType)
    ) &&
    isAmiraIsipPilot &&
    failureReturnUrl
  ) {
    // If we're in pilot mode and there are no assignments or assignment is not an assessment, redirect to the failure return URL
    window.location.href = failureReturnUrl;
    return;
  }

  return transformAssignment(nextAssignment);
}

export function removeCompletedDistrictAssignments(assignments,grade,screenerEnabled,assessmentEnabled){
  let completedBenchmark = assignments.find((studentAssignment)=>completedBenchmarkInCurrentScreeningWindow(studentAssignment,screenerEnabled,assessmentEnabled));
  let schoolAssignment = assignments.find(assignment=>assignment.entityType === ENTITY_TYPE.SCHOOL && assignment.status === ASSIGNMENT_STATUS.ASSIGNED);

  return assignments
  .filter((assignment)=>{
    //Keep all student assignments
    if(assignment.entityType === ENTITY_TYPE.STUDENT){
      return true;
    }

    if(!isWindowEnabledByGrade(grade)) return false;

    //Filter out all school assignments if a student has completed a benchmark
    if(assignment.entityType === ENTITY_TYPE.SCHOOL){
      if(completedBenchmark) return false;
      return assignment.status === ASSIGNMENT_STATUS.ASSIGNED;
    }

    //Filter out all district assignments if a student has completed a benchmark or if there is a school assignment
    if(assignment.entityType === ENTITY_TYPE.DISTRICT){
      if(completedBenchmark || schoolAssignment) return false;
      return assignment.status === ASSIGNMENT_STATUS.ASSIGNED;
    }
  });
}

export function isWindowEnabledByGrade(grade){
  const {windowDisabledByGrade} = getUserData();
  return (!windowDisabledByGrade || windowDisabledByGrade && !windowDisabledByGrade[grade])
}

/**
 * filters to array of assignments with ASSIGNED status
 * @param assignments
 * @returns {array<assignment>}
 */
export function filterPendingAssignments(assignments){
  return assignments.filter(({status}) => [ASSIGNMENT_STATUS.ASSIGNED, ASSIGNMENT_STATUS.IN_PROGRESS].includes(status));
}

/**
 * transforms an assignment to the model expected by the web app
 * @param assignment
 * @returns {object<assignment>}
 */
export function transformAssignment(assignment){
  return assignment ? {
    ...assignment,
    status: assignment.status ? assignment.status : ASSIGNMENT_STATUS.ASSIGNED,
    manifest: assignment.manifest.map(item => {
      return {
        ...item,
        activity: item.activity ? item.activity : [],
        locale: formatLocale_LowerCaseHyphen(item.locale),
        status: item.status ? item.status : ASSIGNMENT_STATUS.ASSIGNED,
        type: item.activityType,
      }
    })
  } : null;
}

function completedBenchmarkInCurrentScreeningWindow(assignment,screenerEnabled,assessmentEnabled){
  if(assignment && (assignment.entityType !== ENTITY_TYPE.STUDENT ||
                    assignment.assignmentType !== ASSIGNMENT_TYPE.BENCHMARK ||
                    !([ASSIGNMENT_STATUS.DONE,ASSIGNMENT_STATUS.COMPLETE].includes(assignment.status)))) return false;
  let benchmarkAssignment = assignment.manifest.find(task=>(task.activityType === "assessment" || (task.activityType === "dyslexia" && !assessmentEnabled)));
  let results = benchmarkAssignment && benchmarkAssignment.activity.reduce((inWindow,activity)=>{
     return inCurrentScreeningWindow(new Date(activity.createdAt)) ? true : inWindow;
  },false);
  results = results && benchmarkAssignment && [ASSIGNMENT_STATUS.DONE,ASSIGNMENT_STATUS.COMPLETE].includes(benchmarkAssignment.status);
  return results;
}

// We do two things in this method:
// 1. Configure the manifest based on the school's settings.
// 2. Override district windows if a school has set one.
async function transformScreeningWindowAssignment(assignment,grade,school,defaultEntities,entityType, schoolYear){
  let requests = [getAssignmentTemplate({entityId: school, entityType: ENTITY_TYPE.SCHOOL, grade: grade === -1 ? GRADES[GRADES.length - 1] : GRADES[grade], defaultEntities})];
  requests.push(getSchoolAssignmentInThisWindow(school, schoolYear));
  let responses = await Promise.all(requests);
  let schoolTemplate = responses[0];
  let schoolAssignment = responses[1];

  // Put the school's manifest (assignment settings) into the district assignment
  assignment.manifest = schoolTemplate.manifest;
  // If there is a school screening window set, override the district's. Even if it is in the past or future.
  if(entityType === ENTITY_TYPE.DISTRICT && schoolAssignment && schoolAssignment.assignment && schoolAssignment.assignment.length){
    assignment.status = ASSIGNMENT_STATUS.UNASSIGNED;
  }
  return assignment;
}

async function getSchoolAssignmentInThisWindow(school, schoolYear){
  // schoolYear is REQUIRED for this query - throw if unavailable
  if (!schoolYear) {
    logSessionInfo("school_year_validation_failed", {
      schoolId: school,
      reason: "school_year_missing",
      location: "getSchoolAssignmentInThisWindow"
    });
    throw new Error('School year is required for school window assignment query but not available.');
  }
  
  let window = getScreeningWindowsForSchoolYear().find((win)=>win.isCurrent);
  let dateTo = window.endDate.toISOString().split('T')[0];
  let dateFrom = window.startDate.toISOString().split('T')[0];
  return graphqlRequest(CHECK_FOR_SCHOOL_WINDOWS,{entityId: school, dateFrom, dateTo, schoolYears: [schoolYear]});
}

async function checkEnabledTasks(assignment,session){
  if(assignment.assignmentType !== ASSIGNMENT_TYPE.BENCHMARK) return assignment;
  let {screenerEnabled, assessmentEnabled, isESL, spanishConfig, assessmentLanguageOverride} = session;
  let manifest = JSON.parse(JSON.stringify(assignment.manifest));

  // Use assessmentLanguageOverride if it's not null or undefined, otherwise use spanishConfig
  let languageConfig = assessmentLanguageOverride !== null && assessmentLanguageOverride !== undefined ? assessmentLanguageOverride : spanishConfig;

  // Make sure to honor the SPANISH and ENGLISH_ONLY configurations. Assignment service has to return both locales because it doesn't know about a
  // student's bilingual status. This block will filter out the wrong locale if a student-assignment hasn't been created yet.
  if((assignment.entityType !== ENTITY_TYPE.STUDENT) && isESL && [SPANISHCONFIG.englishOnly,SPANISHCONFIG.spanishOnly].includes(languageConfig)){
    let locales = languageConfig === SPANISHCONFIG.englishOnly ? ["EN_US"] : ["ES_MX"];
    manifest = transformManifest(manifest,locales,false);
  }
  let cleanedManifest = cleanManifest(manifest);

  let skippedActivities = []
  assignment.manifest = cleanedManifest.filter((task)=>{
    let isEnabled = (task.activityType === 'assessment' && assessmentEnabled) || (task.activityType !== 'assessment' && screenerEnabled);
    // This block allows an assignment to self-heal in English-only and Spanish-only configs if it got created with the wrong settings
    // from a screening window.
    if(isESL && [SPANISHCONFIG.englishOnly,SPANISHCONFIG.spanishOnly].includes(languageConfig) && assignment.parentId){
      let enabledLocale = languageConfig === SPANISHCONFIG.englishOnly ? "EN_US" : "ES_MX";
      if(task.locale !== enabledLocale){
        isEnabled = false;
      }
    }
    if(!isEnabled){
      skippedActivities.push(task);
    }
    return isEnabled;
  });

  if(assignment.entityType === ENTITY_TYPE.STUDENT){
    if(skippedActivities.length){
      await Promise.all(skippedActivities.map(async(task)=>{
        await skipActivityInManifest(assignment.assignmentId,task.activityType,task.locale)
      }));
    }
  }

  return assignment;
}

async function unassignDuplicateStudentAssignments(nextAssignment, assignments){
  const duplicateAssignments = assignments.filter(({entityType, assignmentId, assignmentType, status}) =>
    entityType === ENTITY_TYPE.STUDENT
    && assignmentId !== nextAssignment.assignmentId
    && assignmentType === nextAssignment.assignmentType
    && [ASSIGNMENT_STATUS.ASSIGNED,ASSIGNMENT_STATUS.IN_PROGRESS].includes(status));

  const batchInput = duplicateAssignments.map((assignment)=>{
    return {
      assignmentId: assignment.assignmentId,
      entityId: assignment.entityId,
      entityType: assignment.entityType,
      status: ASSIGNMENT_STATUS.UNASSIGNED,
    }
  });

  if (!batchInput || batchInput.length === 0) {
    return;
  }

  return await updateBatchAssignments(batchInput);
}

export { getSpanishCalibrationAssignment } from './getSpanishCalibrationAssignment';
